import { useEffect, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { appConfig } from '../../config/app'
import type { NavItem } from '../../types'
import UserAvatar from './UserAvatar'
import { usePublicSettings } from '../../features/settings/usePublicSettings'
import { apiOrigin } from '../../lib/apiClient'

interface WorkspaceSidebarProps {
  items: NavItem[]
  mini: boolean
  mobileOpen: boolean
  userName: string
  roleLabel: string
  onClose: () => void
  onSignOut: () => void
}

function MenuItem({ item, mini, onNavigate }: { item: NavItem; mini: boolean; onNavigate: () => void }) {
  const location = useLocation()
  const hasActiveChild = item.children?.some((child) => child.path && location.pathname === child.path) ?? false
  const [open, setOpen] = useState(hasActiveChild)

  useEffect(() => {
    if (hasActiveChild) setOpen(true)
  }, [hasActiveChild])

  if (item.children) {
    return (
      <li className={`sidebar-menu-group ${open ? 'is-open' : ''}`}>
        <button
          className={`sidebar-link ${hasActiveChild ? 'active' : ''}`}
          type="button"
          onClick={() => setOpen((value) => !value)}
          title={mini ? item.title : undefined}
          aria-expanded={open}
        >
          <span className="nav-icon"><i className={`bi ${item.icon ?? 'bi-circle'}`} aria-hidden="true" /></span>
          <span className="nav-text">{item.title}</span>
          <i className="bi bi-chevron-down submenu-chevron" aria-hidden="true" />
        </button>
        {!mini && (
          <ul className="sidebar-submenu">
            {item.children.map((child) => (
              <li key={child.path ?? child.title}>
                <NavLink className={({ isActive }) => `submenu-link ${isActive ? 'active' : ''}`} to={child.path ?? '#'} onClick={onNavigate}>
                  <span className="submenu-dot" />{child.title}
                </NavLink>
              </li>
            ))}
          </ul>
        )}
      </li>
    )
  }

  return (
    <li>
      <NavLink className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`} to={item.path ?? '#'} onClick={onNavigate} title={mini ? item.title : undefined}>
        <span className="nav-icon"><i className={`bi ${item.icon ?? 'bi-circle'}`} aria-hidden="true" /></span>
        <span className="nav-text">{item.title}</span>
      </NavLink>
    </li>
  )
}

export default function WorkspaceSidebar({ items, mini, mobileOpen, userName, roleLabel, onClose, onSignOut }: WorkspaceSidebarProps) {
  const {data:branding}=usePublicSettings()
  return (
    <>
      <aside className={`admin-sidebar ${mini ? 'is-mini' : ''} ${mobileOpen ? 'is-mobile-open' : ''}`} aria-label={`${roleLabel} navigation`}>
        <div className="sidebar-header">
          <NavLink className="sidebar-brand" to="/" aria-label={`${appConfig.name} home`}>
            <span className="brand-icon" aria-hidden="true">{branding?.logo?<img src={apiOrigin+branding.logo} alt="" width={40} height={40}/>:<i className="bi bi-heart-pulse" />}</span>
            <span className="brand-copy"><strong>{branding?.site_name||appConfig.shortName}</strong><small>Online chamber</small></span>
          </NavLink>
        </div>
        <nav className="sidebar-scroll">
          <p className="sidebar-label">{roleLabel}</p>
          <ul className="sidebar-menu">
            {items.map((item) => <MenuItem key={item.title} item={item} mini={mini} onNavigate={onClose} />)}
          </ul>
        </nav>
        <div className="sidebar-user" title={mini ? userName : undefined}>
          <UserAvatar name={userName} />
          <span className="sidebar-user-copy"><strong>{userName}</strong><small><span className="status-dot" /> Signed in</small></span>
          {!mini && <button className="sidebar-signout" type="button" onClick={onSignOut} aria-label="Sign out"><i className="bi bi-box-arrow-right" /></button>}
        </div>
        {!mini && <div className="sidebar-version">{branding?.site_name||appConfig.name} v{appConfig.version}</div>}
      </aside>
      <button className={`sidebar-backdrop ${mobileOpen ? 'show' : ''}`} type="button" aria-label="Close navigation" onClick={onClose} />
    </>
  )
}
